import { useState } from 'react'
import { Mail } from 'lucide-react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { newsletterApi } from '../lib/api'
import { Link } from '../i18n/Link'
import LanguageSwitcher from '../i18n/LanguageSwitcher'

const footerLinks = {
  product: [
    ['features', '/features'],
    ['compare', '/compare'],
    ['pricing', '/pricing'],
    ['download', '/download'],
    ['enterprise', '/enterprise'],
  ],
  company: [
    ['about', '/about'],
    ['blog', '/blog'],
    ['careers', '/careers'],
    ['press', '/press'],
    ['partners', '/partners'],
  ],
  support: [
    ['help', '/help'],
    ['docs', '/docs'],
    ['community', '/community'],
    ['status', '/status'],
    ['changelog', '/changelog'],
    ['contact', '/contact'],
  ],
  legal: [
    ['privacy', '/privacy'],
    ['terms', '/terms'],
    ['security', '/security'],
    ['cookies', '/cookies'],
  ],
} as const

const socialLinks = [
  { icon: 'twitter', href: '#', label: 'Twitter' },
  { icon: 'github', href: 'https://github.com/cmappseu', label: 'GitHub' },
  { icon: 'linkedin', href: '#', label: 'LinkedIn' },
  { icon: 'youtube', href: '#', label: 'YouTube' },
]

const SocialIcon = ({ type }: { type: string }) => {
  const icons: Record<string, React.ReactNode> = {
    twitter: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
    github: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" />
      </svg>
    ),
    linkedin: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
    youtube: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    ),
  }
  return icons[type] || null
}

export default function Footer() {
  const { t } = useTranslation('common')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [step, setStep] = useState<'email' | 'code' | 'done'>('email')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  const handleSendCode = async () => {
    if (!email) return
    setLoading(true)
    setError('')
    try {
      const res = await newsletterApi.subscribe(email)
      if (res.alreadySubscribed) {
        setMessage(t('footer.alreadySubscribed'))
        setStep('done')
      } else {
        setStep('code')
        setMessage(t('footer.codeSent'))
      }
    } catch {
      setError(t('footer.error'))
    } finally {
      setLoading(false)
    }
  }

  const handleVerify = async () => {
    if (!code || code.length !== 6) return
    setLoading(true)
    setError('')
    try {
      await newsletterApi.verify(email, code)
      setStep('done')
      setMessage(t('footer.subscribed'))
      setEmail('')
      setCode('')
    } catch {
      setError(t('footer.invalidCode'))
    } finally {
      setLoading(false)
    }
  }

  const handleResend = async () => {
    setLoading(true)
    setError('')
    try {
      await newsletterApi.resend(email)
      setMessage(t('footer.codeResent'))
    } catch {
      setError(t('footer.wait'))
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    ['product', footerLinks.product],
    ['company', footerLinks.company],
    ['support', footerLinks.support],
    ['legal', footerLinks.legal],
  ] as const

  return (
    <footer className="relative z-10 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-8 lg:gap-12">
          {/* Brand */}
          <div className="col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-6">
              <img src="/brand/mark.svg" alt={t('footer.logoAlt')} className="w-8 h-8" />
              <span className="text-xl font-display font-bold">
                CM<span className="text-primary">Desk</span>
                <span className="block text-xs font-normal tracking-wide text-gray-300">{t('brand.tagline')}</span>
              </span>
            </Link>
            <p className="text-gray-400 text-sm mb-6 max-w-xs">{t('footer.tagline')}</p>
            <div className="flex items-center gap-4">
              {socialLinks.map((social) => (
                <motion.a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className="p-2 text-gray-400 hover:text-primary transition-colors"
                  aria-label={t('footer.social', { name: social.label })}
                >
                  <SocialIcon type={social.icon} />
                </motion.a>
              ))}
            </div>
          </div>

          {columns.map(([key, links]) => (
            <div key={key}>
              <h2 className="text-white font-semibold mb-4">{t(`footer.${key}`)}</h2>
              <ul className="space-y-3">
                {links.map(([page, path]) => (
                  <li key={path}>
                    <Link to={path} className="text-gray-400 hover:text-primary text-sm transition-colors">
                      {t(`pages.${page}`)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Newsletter */}
        <div className="mt-12 pt-8 border-t border-white/5">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="text-white font-semibold mb-1">{t('footer.newsletterTitle')}</h2>
              <p className="text-gray-400 text-sm">{t('footer.newsletterText')}</p>
            </div>

            {step === 'email' && (
              <div className="flex w-full md:w-auto gap-3">
                <div className="relative flex-1 md:w-64">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type="email"
                    name="newsletter_email"
                    id="newsletter-email"
                    autoComplete="email"
                    aria-label={t('footer.emailLabel')}
                    placeholder={t('footer.emailPlaceholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSendCode()}
                    className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-hidden focus:border-primary transition-colors"
                  />
                </div>
                <button
                  onClick={handleSendCode}
                  disabled={loading || !email}
                  className="px-6 py-3 bg-primary hover:bg-primary-dark text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  {loading ? t('footer.sending') : t('footer.subscribe')}
                </button>
              </div>
            )}

            {step === 'code' && (
              <div className="w-full md:w-auto">
                <div className="flex gap-3 mb-2">
                  <input
                    type="text"
                    maxLength={6}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder={t('footer.codePlaceholder')}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    onKeyDown={(e) => e.key === 'Enter' && handleVerify()}
                    className="w-36 px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white text-center text-lg tracking-widest placeholder-gray-500 focus:outline-hidden focus:border-primary transition-colors"
                  />
                  <button
                    onClick={handleVerify}
                    disabled={loading || code.length !== 6}
                    className="px-6 py-3 bg-primary hover:bg-primary-dark text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                  >
                    {loading ? t('footer.verifying') : t('footer.verify')}
                  </button>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-500">{t('footer.noCode')}</span>
                  <button
                    onClick={handleResend}
                    disabled={loading}
                    className="text-primary hover:text-primary-dark transition-colors disabled:opacity-50"
                  >
                    {t('footer.resend')}
                  </button>
                  <span className="text-gray-600 mx-1">|</span>
                  <button
                    onClick={() => {
                      setStep('email')
                      setCode('')
                      setError('')
                      setMessage('')
                    }}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {t('footer.changeEmail')}
                  </button>
                </div>
              </div>
            )}

            {step === 'done' && (
              <div className="flex items-center gap-2 text-success">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="font-medium">{message}</span>
              </div>
            )}
          </div>

          {(message || error) && step !== 'done' && (
            <p className={`text-sm mt-2 text-right ${error ? 'text-red-400' : 'text-gray-400'}`}>{error || message}</p>
          )}
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-8 border-t border-white/5">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-400 text-sm">{t('footer.rights', { year: new Date().getFullYear() })}</p>

            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-sm text-gray-400">
              <LanguageSwitcher />
              <span className="text-gray-600 hidden md:inline">|</span>
              <span>{t('footer.madeIn')}</span>
              <span className="text-gray-600">|</span>
              <span>
                {t('footer.productOf', { company: '' })}
                <a
                  href="https://cmapps.eu"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary-dark font-semibold transition-colors"
                >
                  CMApps
                </a>
              </span>
            </div>
          </div>

          <div className="mt-6 text-center">
            <p className="text-gray-400 text-xs">{t('footer.crafted')}</p>
          </div>
        </div>
      </div>
    </footer>
  )
}
